import React, { useState } from 'react';
import { FaGlobe, FaCopy, FaCheck, FaSpinner, FaTimes } from 'react-icons/fa';
import { createPublicTripLink, revokePublicTripLink, trackEvent } from '../api/client';

/**
 * Publish a finished trip as a public read-only link.
 *
 * A shared itinerary is the best advertisement the product has — it shows the
 * thing working on a real trip rather than describing it — but sharing has
 * until now required the recipient to register, so a link posted anywhere
 * public was a dead end.
 *
 * Publishing is explicit and revocable, and the control says plainly what goes
 * out. Only the plan travels: no members, no expenses, no chat, no photos.
 */
const PublicShareControl = ({ requestId, isOwner }) => {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  if (!isOwner || !requestId) return null;

  const publish = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await createPublicTripLink(requestId);
      setUrl(data.public_url);
      trackEvent('public_trip_published', { requestId });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create a public link.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError('');
    try {
      await revokePublicTripLink(requestId);
      setUrl(null);
      trackEvent('public_trip_unpublished', { requestId });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not withdraw the link.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard blocked — the URL is on screen and selectable.
    }
  };

  return (
    <div className="mt-4 rounded-lg bg-gradient-to-br from-secondary-50 to-secondary-100 p-4">
      <p className="mb-3 text-sm text-secondary-900">
        Or publish a public link anyone can open — no account needed to view it.
        Only the day-by-day plan is shared; your expenses, chat, photos and
        travel companions stay private.
      </p>

      {url ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-secondary-200 bg-white px-3 py-2 text-xs text-neutral-700">
              {url}
            </code>
            <button
              onClick={copy}
              className="inline-flex items-center gap-2 rounded-lg bg-secondary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-secondary-700"
            >
              {copied ? <FaCheck size={12} /> : <FaCopy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            onClick={revoke}
            disabled={busy}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-neutral-500 underline underline-offset-2 hover:text-red-600 disabled:opacity-60"
          >
            <FaTimes size={10} /> Stop sharing publicly
          </button>
        </>
      ) : (
        <button
          onClick={publish}
          disabled={busy}
          className="flex items-center gap-2 rounded-lg bg-secondary-600 px-4 py-2 font-medium text-white shadow-sm transition-colors hover:bg-secondary-700 disabled:opacity-60"
        >
          {busy ? <FaSpinner className="animate-spin" /> : <FaGlobe />}
          Create public link
        </button>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default PublicShareControl;
